import { useState, useEffect, useRef } from "react";
import {
  TextField,
  MenuItem,
  Select,
  FormControl,
  Autocomplete,
  IconButton,
  ListSubheader,
  Checkbox,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useForm, Controller } from "react-hook-form";
import { IoIosArrowBack } from "react-icons/io";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { BtnLoader } from "../../components/common/Loader";
import { useFetchGetallUserListQuery } from "../../slice/logdetailSlice";
import { fetchAllEntities } from "../../apis/apiService";
import { useCreateAwardMutation, useUpdateAwardMutation } from "../../slice/logdetailSlice";
import QuillComponent from "../../components/common/ReactQuill";
import { useTheme } from "../../ThemeContext";
import "../../App.css";

export const AddCertificate = ({ onCancel, fetchCertificates, setSelectedYear, isEdit = false, editData = null }) => {
  const { isDarkMode } = useTheme();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [quarterOptions, setQuarterOptions] = useState([]);
  const [loadingPeriods, setLoadingPeriods] = useState(false);
  const [awardOptions, setAwardOptions] = useState([]);
  const [loadingAwards, setLoadingAwards] = useState(false);
  const [expandedAwards, setExpandedAwards] = useState({});

  // Fetch all users via getAllUserDetails API using RTK Query
  const { data: allUsers, isLoading: loadingEmployees } =
    useFetchGetallUserListQuery();

  const [createAward] = useCreateAwardMutation();
  const [updateAward] = useUpdateAwardMutation();
  const maxSelectableYear = dayjs().add(1, "year").endOf("year");
  const maxAllowedYear = dayjs().add(1, "year").year();

  // Direct employee list from getAllUserDetails (each user has user_id & name)
  const employeeOptions = Array.isArray(allUsers) ? allUsers : [];

  const periodsFetched = useRef(false);
  const awardsFetched = useRef(false);

  // Fetch award periods from API for Quarter dropdown
  useEffect(() => {
    if (periodsFetched.current) return;
    periodsFetched.current = true;

    const fetchAwardPeriods = async () => {
      setLoadingPeriods(true);
      try {
        const response = await fetchAllEntities("master/award-periods/show");
        if (response && response.data) {
          const periodsArray = Array.isArray(response.data)
            ? response.data
            : Object.values(response.data);
          
          const normalized = periodsArray.map(p => ({
            ...p,
            period_id: p.period_id || p.id || p.award_period_id,
            period_label: p.period_label || p.label
          }));
          setQuarterOptions(normalized);
        }
      } 
      catch (err) {
        console.error("Error fetching award periods:", err);
      } 
      finally {
        setLoadingPeriods(false);
      }
    };
    fetchAwardPeriods();
  }, []);

  // Fetch awards from API for Award Name dropdown
  useEffect(() => {
    if (awardsFetched.current) return;
    awardsFetched.current = true;
    const fetchAwards = async () => {
      setLoadingAwards(true);
      try {
        const response = await fetchAllEntities("master/awards/show");
        if (response && response.data) {
          const awardsArray = Array.isArray(response.data)
            ? response.data
            : Object.values(response.data);
          
          const normalized = awardsArray.map(a => ({
            ...a,
            award_id: a.award_id || a.id,
            award_name: a.award_name || a.name
          }));
          setAwardOptions(normalized);
        }
      } 
      catch (err) {
        console.error("Error fetching awards:", err);
      } 
      finally {
        setLoadingAwards(false);
      }
    };
    fetchAwards();
  }, []);

  const {
    handleSubmit,
    control,
    reset,
    trigger,
    setValue,
  } = useForm({
    defaultValues: {
      employee_id: isEdit ? "" : [],
      quarter: "",
      year: dayjs(),
      award_name: "",
      award_category: "",
      award_date_issued: dayjs(),
      achievement_summary: "",
    },
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  useEffect(() => {
    if (isEdit && editData) {
      // Check if the date is just a placeholder (January 1st fallback from the table)
      const incomingDate = editData.award_date_issued;
      const isPlaceholderDate = incomingDate && incomingDate.endsWith("-01-01");

      reset({
        employee_id: editData.employee_id || "",
        quarter: editData.period_id ? String(editData.period_id) : "",
        // If it's a placeholder "01-01" or null, show Current Date (today)
        award_date_issued: (!incomingDate || isPlaceholderDate) ? dayjs() : dayjs(incomingDate),
        year: editData.award_year ? dayjs(`${editData.award_year}-01-01`) : dayjs(),
        award_name: editData.award_id ? (editData.period_id ? `${editData.award_id}|${editData.period_id}` : String(editData.award_id)) : "",
        achievement_summary: editData.achievement_summary ? editData.achievement_summary.replace(/&lt;/g, "<").replace(/&gt;/g, ">") : "",
      });
    }
  }, [isEdit, editData, reset]);

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      // Look up the award_name string from the selected award_id
      const valueStr = String(data.award_name);
      const selAwardId = valueStr.includes('|') ? valueStr.split('|')[0] : valueStr;
      const selectedAward = awardOptions.find(
        (a) => String(a.award_id) === String(selAwardId)
      );

      // Base fields required by backend rules
      const selectedPeriodObj = quarterOptions.find(q => String(q.period_id) === String(data.quarter));

      const payload = {
        award_id: Number(selAwardId),
        award_name: selectedAward?.award_name || "",
        description: data.achievement_summary ? data.achievement_summary.replace(/</g, "&lt;").replace(/>/g, "&gt;") : "",
        award_year: data.year ? dayjs(data.year).year() : (data.award_date_issued ? dayjs(data.award_date_issued).year() : null),
        awarded_date: data.award_date_issued ? dayjs(data.award_date_issued).format("YYYY-MM-DD") : null,
        period_id: Number(data.quarter),
        period: selectedPeriodObj?.period_label || ""
      };

      if (isEdit) {
        // Backend requires employee_id as integer for single employee update
        payload.employee_id = Number(data.employee_id);
      } else {
        // Creation fields
        payload.employees = Array.isArray(data.employee_id) ? data.employee_id.map(id => Number(id)) : [];
      }

      let response;
      if (isEdit) {
        // Pass award_employee_id to the mutation for the URL path
        response = await updateAward({
          ...payload,
          award_employee_id: Number(editData.award_employee_id)
        }).unwrap();
        toast.success(response?.message || "Award updated successfully!");
      } 
      else {
        response = await createAward(payload).unwrap();
        toast.success(response?.message || "Award created successfully!");
      }

      // Update the parent's year filter to the new/updated record's year
      if (setSelectedYear) {
        const yearVal = data.year ? dayjs(data.year) : (data.award_date_issued ? dayjs(data.award_date_issued) : dayjs());
        setSelectedYear(yearVal);
      }

      if (fetchCertificates && typeof fetchCertificates === "function") {
        await fetchCertificates();
      }
      onCancel();
      reset();
    } 
    catch (error) {
      toast.error(
        error?.data?.message || error?.message || "Failed to process award. Please try again."
      );
    } 
    finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between filter-h-bg px-4 py-3">
        <button onClick={onCancel} className="R_btn">
          <div className="d-flex text-rgba-gray align-items-center form-label">
            <IoIosArrowBack className="me-1" />
            {isEdit ? "Edit Certificate" : "Generate Certificate"}
          </div>
        </button>
      </div>

      {/* Form Body */}
      <div className="bg-white pt-3 px-4" style={{ flex: 1, overflowY: "auto" }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div>

              {/* EMPLOYEE NAME */}
              <div className="row mb-3">
                <div className="col-md-12">
                  <label className="form-label">
                    Employee Name <span className="text-rgbr-red">*</span>
                  </label>
                  <Controller
                    name="employee_id"
                    control={control}
                    rules={{ required: "Employee is required" }}
                    render={({ field, fieldState }) => (
                      <Autocomplete
                        multiple={!isEdit}
                        options={employeeOptions}
                        loading={loadingEmployees}
                        getOptionLabel={(option) =>
                          typeof option === "string"
                            ? option
                            : option?.name || ""
                        }
                        value={
                          isEdit
                            ? (employeeOptions.find((u) => u.user_id === field.value) || null)
                            : (employeeOptions.filter((u) => Array.isArray(field.value) && field.value.includes(u.user_id)))
                        }
                        onChange={(event, newValue) => {
                          if (isEdit) {
                            field.onChange(newValue ? newValue.user_id : "");
                          } else {
                            field.onChange(newValue ? newValue.map(v => v.user_id) : []);
                          }
                        }}
                        disableCloseOnSelect={!isEdit}
                        renderOption={(props, option, { selected }) => {
                          const { key, ...optionProps } = props;
                          return (
                            <li key={key} {...optionProps}>
                              {!isEdit && (
                                <Checkbox
                                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                                  style={{ marginRight: 8 }}
                                  checked={selected}
                                />
                              )}
                              {option.name}
                            </li>
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder={
                              isEdit
                                ? (field.value ? "" : "Select Employee")
                                : (Array.isArray(field.value) && field.value.length > 0 ? "" : "Select Employees")
                            }
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                            slotProps={{
                              formHelperText: {
                                className: "CertificateErrorMsg",
                                sx: { marginLeft: 0 }
                              }
                            }}
                          />
                          // <TextField
                          //     {...params}
                          //     placeholder={
                          //         isEdit
                          //             ? (field.value ? "" : "Select Employee")
                          //             : (Array.isArray(field.value) && field.value.length > 0 ? "" : "Select Employees")
                          //     }
                          //     error={!!fieldState.error}
                          //     helperText={fieldState.error?.message}
                          //     FormHelperTextProps={{
                          //         className: "CertificateErrorMsg",
                          //         sx: { marginLeft: 0 }
                          //     }}
                          // />
                        )}
                      />
                    )}
                  />
                </div>
              </div>

              {/* QUARTER + YEAR */}
              <div className="row mb-3">
                {/* Quarter */}
                <div className="col-md-6">
                  <label className="form-label ">
                    Quarter Name <span className="text-rgbr-red">*</span>
                  </label>
                  <Controller
                    name="quarter"
                    control={control} FormHelperTextProps
                    rules={{ required: "Quarter is required" }}
                    render={({ field, fieldState }) => (
                      <>
                        <FormControl fullWidth error={!!fieldState.error}>
                          <Select
                            {...field}
                            displayEmpty
                            value={field.value || ""}
                            sx={{
                              '& .clear-icon': { opacity: 0, transition: '0.2s' },
                              '&:hover .clear-icon': { opacity: 1 }
                            }}
                            renderValue={(selected) => {
                              if (!selected || selected === "") {
                                return <span style={{ color: "#999999" }}>Select Quarter</span>;
                              }
                              const q = quarterOptions.find((opt) => String(opt.period_id) === String(selected));
                              return q ? q.period_label : selected;
                            }}
                            endAdornment={
                              field.value ? (
                                <IconButton
                                  className="clear-icon text-rgbr-red"
                                  size="small"
                                  sx={{
                                    position: 'absolute',
                                    right: 28,
                                    '&:hover': { color: 'error.main' }
                                  }}
                                  onMouseDown={(e) => e.stopPropagation()}
                                  onClick={() => field.onChange("")}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              ) : null
                            }
                          >
                            <MenuItem value="" disabled style={{ display: 'none' }}>
                              Select Quarter
                            </MenuItem>
                            {quarterOptions.map((q) => (
                              <MenuItem key={q.period_id} value={String(q.period_id)}>
                                {q.period_label}
                              </MenuItem>
                            ))}
                            {isEdit && editData?.period_id && !quarterOptions.some(q => String(q.period_id) === String(editData.period_id)) && (
                              <MenuItem value={String(editData.period_id)} style={{ display: 'none' }}>
                                {editData.quarter}
                              </MenuItem>
                            )}
                          </Select>
                        </FormControl>
                        {fieldState.error && (
                          <span className="CertificateErrorMsg">
                            {fieldState.error.message}
                          </span>
                        )}
                      </>
                    )}
                  />
                </div>

                {/* Award Date Issued */}
                <div className="col-md-6">
                  <label className="form-label">
                    Award Date <span className="text-rgbr-red">*</span>
                  </label>

                  <div className="date-of-incident d-flex flex-column w-full position-relative">
                    {/* <Controller
      name="award_date_issued"
      control={control}
      rules={{
        required: "Award Date is required",
        validate: (value) => {
          if (!value) return "Award Date is required.";

          const date = dayjs(value);
          const currentDate = dayjs();

          //  Future date check
          if (date.isAfter(currentDate, "day")) {
            return "Future dates are not allowed.";
          }

          //  Year < 2000 check
          if (date.year() < 2000) {
            return "Year must be 2000 or later.";
          }

          return true;
        }
      }}
      render={({ field, fieldState }) => (
        <DatePicker
          value={field.value || null}
          format="DD/MM/YYYY"

          //  Restrict range
          minDate={dayjs("2000-01-01")}
          maxDate={dayjs()} //  disables future years dynamically

          disableFuture //  extra safety

          disablePortal

          onOpen={() => {
            const scrollBarWidth =
              window.innerWidth - document.documentElement.clientWidth;
            document.body.style.overflow = "hidden";
            document.body.style.paddingRight = `${scrollBarWidth}px`;
          }}

          onClose={() => {
            document.body.style.overflow = "";
            document.body.style.paddingRight = "";
            setTimeout(() => trigger("award_date_issued"), 0);
          }}

          onChange={(date) => {
            field.onChange(date);
            setTimeout(() => trigger("award_date_issued"), 0);
          }}

          sx={{
            "& .MuiPickersDay-root.Mui-disabled": {
              color: (theme) =>
                theme.palette.mode === "dark" ? "#555555" : undefined,
              opacity: (theme) =>
                theme.palette.mode === "dark" ? 0.4 : undefined,
            },
          }}

          slotProps={{
            textField: {
              error: !!fieldState.error,
              helperText: fieldState.error?.message || "",
              onBlur: () => trigger("award_date_issued"),
            },
            popper: {
              placement: "bottom-start",
              modifiers: [
                { name: "eventListeners", options: { scroll: true, resize: true } },
                { name: "preventOverflow", options: { altAxis: true, tether: false } },
                { name: "flip", options: { fallbackPlacements: ["top-start"] } },
                { name: "hide", enabled: true },
              ],
            },
          }}

          className="custom-disabled-datepicker"
        />
      )}
    /> */}
                    <Controller
                      name="award_date_issued"
                      control={control}
                      rules={{
                        required: "Award Date is required",
                        validate: (value) => {
                          if (!value) return "Award Date is required.";

                          const date = dayjs(value);

                          if (!date.isValid()) {
                            return "Please enter a valid date.";
                          }

                          if (date.year() < 2000) {
                            return "Year must be 2000 or later.";
                          }

                          if (date.isAfter(maxSelectableYear, "day")) {
                            return `Date cannot be later than ${maxSelectableYear.format("DD/MM/YYYY")}.`;
                          }

                          return true;
                        }
                      }}
                      render={({ field, fieldState }) => (
                        <DatePicker
                          value={field.value || null}
                          format="DD/MM/YYYY"
                          minDate={dayjs("2000-01-01")}
                          maxDate={maxSelectableYear}

                          // 🔥 KEY FIX: real-time validation
                          onChange={(date) => {
                            field.onChange(date);
                            trigger("award_date_issued"); // ✅ instant validation
                          }}

                          slotProps={{
                            textField: {
                              error: !!fieldState.error,
                              helperText: fieldState.error?.message || "",
                              onBlur: () => trigger("award_date_issued"),

                              sx: {
                                "& .MuiOutlinedInput-root": {
                                  backgroundColor: isDarkMode ? "#1f1f1f" : "#ffffff",
                                  color: isDarkMode ? "#ffffff" : "inherit",
                                  "& fieldset": {
                                    borderColor: fieldState.error ? "#d32f2f" : "",
                                  },
                                  "&:hover fieldset": {
                                    borderColor: fieldState.error ? "#d32f2f" : "",
                                  },
                                  "&.Mui-focused fieldset": {
                                    borderColor: fieldState.error ? "#d32f2f" : "rgba(18, 183, 159, 1)",
                                  },
                                },
                                "& .MuiInputBase-input": {
                                  color: isDarkMode ? "#ffffff" : "inherit",
                                },
                                "& .MuiSvgIcon-root": {
                                  color: isDarkMode ? "#ffffff" : "inherit",
                                },
                              },
                            },
                            popper: {
                              sx: {
                                ...(isDarkMode ? {
                                  "& .MuiPaper-root": {
                                    backgroundColor: "#1f1f1f",
                                    color: "#ffffff",
                                  },
                                  "& .MuiPickersCalendarHeader-root, & .MuiDayCalendar-weekDayLabel, & .MuiPickersArrowSwitcher-button, & .MuiPickersCalendarHeader-switchViewButton": {
                                    color: "#ffffff",
                                  },
                                } : {}),
                                // Day Pickers
                                "& .MuiPickersDay-root": {
                                  color: isDarkMode ? "#ffffff" : "inherit",
                                },
                                "& .MuiPickersDay-root:hover": {
                                  backgroundColor: "rgba(18, 183, 159, 0.18)",
                                },
                                "& .MuiPickersDay-root.Mui-selected": {
                                  backgroundColor: "rgba(18, 183, 159, 1) !important",
                                  color: "#ffffff !important",
                                },
                                "& .MuiPickersDay-root.Mui-selected:hover": {
                                  backgroundColor: "rgba(16, 160, 139, 1) !important",
                                },
                                // Year & Month Pickers
                                "& .MuiPickersYear-yearButton, & .MuiPickersMonth-monthButton": {
                                  color: isDarkMode ? "#ffffff" : "inherit",
                                  borderRadius: "18px",
                                },
                                "& .MuiPickersYear-yearButton:hover, & .MuiPickersMonth-monthButton:hover": {
                                  backgroundColor: "rgba(18, 183, 159, 0.18)",
                                },
                                "& .MuiPickersYear-yearButton.Mui-selected, & .MuiPickersMonth-monthButton.Mui-selected": {
                                  backgroundColor: "rgba(18, 183, 159, 1) !important",
                                  color: "#ffffff !important",
                                  fontWeight: 600,
                                },
                                "& .MuiPickersYear-yearButton.Mui-selected:hover, & .MuiPickersMonth-monthButton.Mui-selected:hover": {
                                  backgroundColor: "rgba(16, 160, 139, 1) !important",
                                },
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </div>
                </div>
              </div>

              {/* AWARD YEAR */}
              <div className="row mb-3">

                <div className="col-md-6">
                  <label className="form-label">
                    Award Year <span className="text-rgbr-red">*</span>
                  </label>

                  <div className="date-of-incident d-flex flex-column w-full position-relative">
                    <Controller
                      name="year"
                      control={control}
                      rules={{
                        required: "Award Year is required",
                        validate: (value) => {
                          if (!value) return "Award Year is required.";

                          const year = dayjs(value).year();

                          //  invalid year
                          if (!year || String(year).length !== 4) {
                            return "Please enter a valid year.";
                          }

                          //  less than 2000
                          if (year < 2000) {
                            return "Year must be 2000 or later.";
                          }

                          //  allow the upcoming next year as well
                          if (year > maxAllowedYear) {
                            return `Year cannot be later than ${maxAllowedYear}.`;
                          }

                          return true;
                        }
                      }}
                      render={({ field, fieldState }) => (
                        <DatePicker
                          views={['year']}
                          value={field.value || null}
                          format="YYYY"

                          //  Restrict range
                          minDate={dayjs("2000-01-01")}
                          maxDate={maxSelectableYear}

                          disablePortal

                          onChange={(date) => {
                            field.onChange(date);
                          }}

                          slotProps={{
                            textField: {
                              error: !!fieldState.error,
                              helperText: fieldState.error?.message || "",
                              onBlur: () => {
                                // trigger validation if typing manually
                                if (field.value) {
                                  field.onChange(field.value);
                                }
                              },
                              sx: {
                                "& .MuiOutlinedInput-root": {
                                  backgroundColor: isDarkMode ? "#1f1f1f" : "#ffffff",
                                  color: isDarkMode ? "#ffffff" : "inherit",
                                  "&.Mui-focused fieldset": {
                                    borderColor: "rgba(18, 183, 159, 1)",
                                  },
                                },
                                "& .MuiInputBase-input": {
                                  color: isDarkMode ? "#ffffff" : "inherit",
                                },
                                "& .MuiSvgIcon-root": {
                                  color: isDarkMode ? "#ffffff" : "inherit",
                                },
                              }
                            },
                            popper: {
                              sx: {
                                ...(isDarkMode ? {
                                  "& .MuiPaper-root": {
                                    backgroundColor: "#1f1f1f",
                                    color: "#ffffff",
                                  },
                                } : {}),
                                "& .MuiPickersYear-yearButton": {
                                  color: isDarkMode ? "#ffffff" : "inherit",
                                  borderRadius: "18px",
                                },
                                "& .MuiPickersYear-yearButton:hover": {
                                  backgroundColor: "rgba(18, 183, 159, 0.18)",
                                },
                                "& .MuiPickersYear-yearButton.Mui-selected": {
                                  backgroundColor: "rgba(18, 183, 159, 1) !important",
                                  color: "#ffffff !important",
                                  fontWeight: 600,
                                },
                                "& .MuiPickersYear-yearButton.Mui-selected:hover": {
                                  backgroundColor: "rgba(16, 160, 139, 1) !important",
                                },
                                "& .MuiPickersYear-yearButton.Mui-selected:focus": {
                                  backgroundColor: "rgba(18, 183, 159, 1) !important",
                                },
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </div>
                </div>

                {/* AWARD NAME */}
                <div className="col-md-6">
                  <label className="form-label">
                    Award Name <span className="text-rgbr-red">*</span>
                  </label>
                  <Controller
                    name="award_name"
                    control={control}
                    rules={{ required: "Award Name is required" }}
                    render={({ field, fieldState }) => (
                      <>
                        <FormControl fullWidth error={!!fieldState.error}>
                          <Select
                            {...field}
                            displayEmpty
                            value={field.value || ""}
                            sx={{
                              '& .clear-icon': { opacity: 0, transition: '0.2s' },
                              '&:hover .clear-icon': { opacity: 1 }
                            }}
                            endAdornment={
                              field.value ? (
                                <IconButton
                                  className="clear-icon text-rgbr-red"
                                  size="small"
                                  sx={{
                                    position: 'absolute',
                                    right: 28,
                                    '&:hover': { color: 'error.main' }
                                  }}
                                  onMouseDown={(e) => e.stopPropagation()}
                                  onClick={() => {
                                    field.onChange("");
                                    setValue("achievement_summary", "");
                                  }}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              ) : null
                            }
                            renderValue={(selected) => {
                              if (!selected || selected === "") {
                                return <span style={{ color: "#999999" }}>Select Award Name</span>;
                              }
                              const parts = String(selected).split('|');
                              const awardId = parts[0];
                              const periodId = parts[1];

                              const selAward = awardOptions.find(a => String(a.id ?? a.award_id) === String(awardId));
                              let label = selAward ? selAward.award_name : (isEdit && editData?.award_name ? editData.award_name : selected);

                              if (selAward && periodId) {
                                const period = selAward.periods?.find(p => String(p.period_id) === String(periodId));
                                if (period) {
                                  label += ` - ${period.period_label}`;
                                }
                              }
                              return label;
                            }}
                            onChange={(e) => {
                              const selectedVal = e.target.value;
                              field.onChange(selectedVal);

                              const parts = String(selectedVal).split('|');
                              const selAwardId = parts[0];
                              const selPeriodId = parts[1];

                              const selectedAward = awardOptions.find(
                                (a) => String(a.id ?? a.award_id) === String(selAwardId)
                              );
                              if (selectedAward) {
                                setValue(
                                  "achievement_summary",
                                  selectedAward.description ? selectedAward.description.replace(/&lt;/g, "<").replace(/&gt;/g, ">") : ""
                                );
                              }
                              if (selPeriodId) {
                                setValue("quarter", selPeriodId);
                              }
                            }}
                          >
                            <MenuItem value="" disabled style={{ display: 'none' }}>
                              Select Award Name
                            </MenuItem>
                            {awardOptions.flatMap((a) => {
                              const awardIdStr = String(a.id ?? a.award_id);
                              const hasPeriods = a.periods && a.periods.length > 0;
                              const isExpanded = !!expandedAwards[awardIdStr];

                              if (!hasPeriods) {
                                return [
                                  <MenuItem key={awardIdStr} value={awardIdStr}>
                                    {a.award_name}
                                  </MenuItem>
                                ];
                              }

                              return [
                                <ListSubheader
                                  key={`header-${awardIdStr}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedAwards(prev => ({
                                      ...prev,
                                      [awardIdStr]: !prev[awardIdStr]
                                    }));
                                  }}
                                  style={{
                                    cursor: 'pointer',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    lineHeight: '40px',
                                    color: '#000',
                                    fontWeight: 600,
                                    backgroundColor: '#f9f9f9',
                                    borderBottom: '1px solid #eaeaea'
                                  }}
                                >
                                  {a.award_name}
                                  {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                </ListSubheader>,
                                ...(isExpanded ? (a.periods || []).map(p => (
                                  <MenuItem
                                    key={`${awardIdStr}|${p.period_id}`}
                                    value={`${awardIdStr}|${p.period_id}`}
                                    style={{ paddingLeft: '32px' }}
                                  >
                                    {p.period_label}
                                  </MenuItem>
                                )) : [])
                              ];
                            })}
                            {isEdit && editData?.award_id && !awardOptions.some(a => String(a.id ?? a.award_id) === String(editData.award_id)) && (
                              <MenuItem value={editData.period_id ? `${editData.award_id}|${editData.period_id}` : String(editData.award_id)} style={{ display: 'none' }}>
                                {editData.award_name}
                              </MenuItem>
                            )}
                          </Select>
                        </FormControl>
                        {fieldState.error && (
                          <span className="CertificateErrorMsg">
                            {fieldState.error.message}
                          </span>
                        )}
                      </>
                    )}
                  />
                </div>
              </div>

              {/* <div className="row mb-3">
                                
                            </div> */}

              {/* ACHIEVEMENT SUMMARY */}
              {/* <div className="row mb-3">
                                <div className="col-md-12">
                                    <label className="form-label">
                                        Achievement Description{" "}
                                        <span className="text-rgbr-red">*</span>
                                    </label>
                                    <Controller
                                        name="achievement_summary"
                                        control={control}
                                        rules={{
                                            required: "Achievement Summary is required",
                                            validate: (value) => {
                                                const text = value ? value.replace(/<(.|\n)*?>/g, "").trim() : "";
                                                return text.length > 0 || "Achievement Summary is required";
                                            }
                                        }}
                                        render={({ field, fieldState }) => (
                                            <>
                                                <QuillComponent
                                                    control={control}
                                                    name="achievement_summary"
                                                    placeholder="Enter achievement summary..."
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    onBlur={field.onBlur}
                                                    ref={field.ref}
                                                />
                                                {fieldState.error && (
                                                    <span className="CertificateErrorMsg">
                                                        {fieldState.error.message}
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    />
                                </div>
                            </div> */}

            </div>

            <br />


          </form>
        </LocalizationProvider>
      </div>

      {/* Fixed Footer Buttons */}
      <div className="bg-white px-4 py-3">
        <div className="d-flex justify-content-start">
          <button
            type="button"
            className="add_btn me-3"
            disabled={isSubmitting}
            onClick={handleSubmit(onSubmit)}
          >
            {isSubmitting ? <BtnLoader /> : (isEdit ? "Update" : "Generate")}
          </button>

          <button
            type="button"
            className="cancel_btn"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};
